import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Info, CalendarRange, ChevronDown, ChevronRight } from "lucide-react";
import { DateInputIT } from "@/components/ui/date-input-it";
import { errMsg } from "@/lib/errors";

const DOW_LABELS = ["lun", "mar", "mer", "gio", "ven", "sab", "dom"];

const formatLong = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return iso;
  }
};

/**
 * Booking-extranet style "Copia le tariffe nelle date future".
 * - Source period (year preset or custom range)
 * - Target period auto-computed (next year by default, editable)
 * - Days of week toggle
 * - Per-room per-rate-plan selection
 */
export default function CopyRatesDialog({ open, onOpenChange, rooms = [], ratePlans = [] }) {
  const today = new Date();
  const Y = today.getFullYear();

  const [yearPreset, setYearPreset] = useState(String(Y));
  const [srcFrom, setSrcFrom] = useState(`${Y}-01-01`);
  const [srcTo, setSrcTo] = useState(`${Y}-12-31`);
  const [tgtFrom, setTgtFrom] = useState(`${Y + 1}-01-01`);
  const [tgtTo, setTgtTo] = useState(`${Y + 1}-12-31`);
  const [dows, setDows] = useState([true, true, true, true, true, true, true]);
  const [overwrite, setOverwrite] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Selection: { [roomId]: { planIds: Set, allPlans: bool } } + master "all rooms"
  const [allRooms, setAllRooms] = useState(true);
  const [perRoom, setPerRoom] = useState({}); // roomId -> { allPlans: bool, planIds: string[] }
  const [openRoom, setOpenRoom] = useState({}); // roomId -> bool (UI expand)

  // Reset state when opening
  useEffect(() => {
    if (!open) return;
    const Y2 = new Date().getFullYear();
    setYearPreset(String(Y2));
    setSrcFrom(`${Y2}-01-01`);
    setSrcTo(`${Y2}-12-31`);
    setTgtFrom(`${Y2 + 1}-01-01`);
    setTgtTo(`${Y2 + 1}-12-31`);
    setDows([true, true, true, true, true, true, true]);
    setOverwrite(true);
    setAllRooms(true);
    const init = {};
    rooms.forEach((r) => { init[r.id] = { allPlans: true, planIds: [] }; });
    setPerRoom(init);
    setOpenRoom({});
  }, [open, rooms]);

  // Sync target year when source year preset changes
  useEffect(() => {
    const m = /^(\d{4})$/.exec(yearPreset || "");
    if (m) {
      const yy = parseInt(m[1], 10);
      setSrcFrom(`${yy}-01-01`);
      setSrcTo(`${yy}-12-31`);
      setTgtFrom(`${yy + 1}-01-01`);
      setTgtTo(`${yy + 1}-12-31`);
    }
  }, [yearPreset]);

  const tgtYearLabel = useMemo(() => {
    const m = /^(\d{4})/.exec(tgtFrom || "");
    return m ? m[1] : "";
  }, [tgtFrom]);

  const toggleDow = (i) => setDows((p) => p.map((v, j) => (j === i ? !v : v)));
  const setAllDows = (v) => setDows([v, v, v, v, v, v, v]);
  const dowsAll = dows.every(Boolean);

  const toggleRoom = (rid) => {
    setPerRoom((p) => ({
      ...p,
      [rid]: { ...(p[rid] || { planIds: [] }), allPlans: !(p[rid]?.allPlans), planIds: [] },
    }));
  };
  const togglePlan = (rid, pid) => {
    setPerRoom((p) => {
      const cur = p[rid] || { allPlans: false, planIds: [] };
      const has = cur.planIds.includes(pid);
      const next = { ...cur, allPlans: false, planIds: has ? cur.planIds.filter((x) => x !== pid) : [...cur.planIds, pid] };
      return { ...p, [rid]: next };
    });
  };

  const apply = async () => {
    // Build room_ids list: if allRooms, send empty (= all rooms). Else only rooms that have any plan selected.
    let room_ids = [];
    if (!allRooms) {
      room_ids = rooms
        .filter((r) => {
          const sel = perRoom[r.id];
          return sel && (sel.allPlans || (sel.planIds && sel.planIds.length > 0));
        })
        .map((r) => r.id);
      if (room_ids.length === 0) {
        toast.error("Seleziona almeno una tipologia di alloggio");
        return;
      }
    }
    const days_of_week = dows.map((v, i) => (v ? i : -1)).filter((x) => x >= 0);

    setSubmitting(true);
    try {
      const res = await api.post("/admin/calendar/copy-rates", {
        source_from: srcFrom,
        source_to: srcTo,
        target_from: tgtFrom,
        target_to: tgtTo,
        days_of_week,
        room_ids,
        overwrite,
      });
      const { copied = 0, skipped = 0 } = res.data || {};
      toast.success(`Copiate ${copied} tariffe${skipped ? ` (${skipped} saltate)` : ""}`);
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e, "Errore durante la copia"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="copy-rates-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#1E3A5F] flex items-center gap-2">
            <Copy className="w-5 h-5 text-[#AC5D45]" />Copia le tariffe nelle date future
          </DialogTitle>
          <DialogDescription className="text-sm text-[#635F59]">
            Qui puoi copiare i tuoi piani tariffari esistenti per il periodo che ti interessa, in maniera facile ed efficiente.
          </DialogDescription>
        </DialogHeader>

        {/* SECTION 1: source dates */}
        <section className="space-y-3">
          <h3 className="font-semibold text-[#1E3A5F] text-sm flex items-center gap-2">
            <CalendarRange className="w-4 h-4 text-[#AC5D45]" />
            Da quali date vuoi copiare le tariffe?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-1">
              <Label className="text-xs">Seleziona il periodo</Label>
              <Select value={yearPreset} onValueChange={setYearPreset}>
                <SelectTrigger className="h-10 mt-1.5" data-testid="cr-year-preset"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={String(Y - 1)}>Intero {Y - 1}</SelectItem>
                  <SelectItem value={String(Y)}>Intero {Y}</SelectItem>
                  <SelectItem value={String(Y + 1)}>Intero {Y + 1}</SelectItem>
                  <SelectItem value="custom">Personalizzato</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-1">
              <Label className="text-xs">Da</Label>
              <DateInputIT value={srcFrom} onChange={(e) => { setSrcFrom(e.target.value); setYearPreset("custom"); }} data-testid="cr-src-from" className="h-10 mt-1.5" />
            </div>
            <div className="md:col-span-1">
              <Label className="text-xs">A</Label>
              <DateInputIT value={srcTo} onChange={(e) => { setSrcTo(e.target.value); setYearPreset("custom"); }} data-testid="cr-src-to" className="h-10 mt-1.5" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Copia verso · Da</Label>
              <DateInputIT value={tgtFrom} onChange={(e) => setTgtFrom(e.target.value)} data-testid="cr-tgt-from" className="h-10 mt-1.5" />
            </div>
            <div>
              <Label className="text-xs">Copia verso · A</Label>
              <DateInputIT value={tgtTo} onChange={(e) => setTgtTo(e.target.value)} data-testid="cr-tgt-to" className="h-10 mt-1.5" />
            </div>
          </div>

          <div className="flex gap-2 items-start bg-orange-50 border border-orange-200 rounded-lg p-3 text-orange-900">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p className="text-xs">
              Le tariffe saranno copiate <strong>per il {tgtYearLabel || "periodo selezionato"}</strong>.
              <br />Sorgente: {formatLong(srcFrom)} → {formatLong(srcTo)}
              <br />Destinazione: {formatLong(tgtFrom)} → {formatLong(tgtTo)}
            </p>
          </div>
        </section>

        {/* SECTION 2: days of week */}
        <section className="space-y-3 pt-2 border-t border-[#E8E2D9]">
          <h3 className="font-semibold text-[#1E3A5F] text-sm">
            Per quali giorni della settimana vuoi copiare le tariffe?
          </h3>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setAllDows(!dowsAll)}
              className="text-[11px] text-[#AC5D45] hover:underline"
              data-testid="cr-toggle-all-dows"
            >
              {dowsAll ? "Deseleziona tutti" : "Seleziona tutti"}
            </button>
            <div className="flex gap-2 flex-wrap">
              {DOW_LABELS.map((d, i) => (
                <label
                  key={d}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded border-2 cursor-pointer text-xs transition ${dows[i] ? "border-[#1E3A5F] bg-[#1E3A5F] text-white" : "border-[#E8E2D9] bg-white text-[#635F59] hover:bg-[#FDFBF7]"}`}
                  data-testid={`cr-dow-${d}`}
                >
                  <Checkbox
                    checked={dows[i]}
                    onCheckedChange={() => toggleDow(i)}
                    className={dows[i] ? "border-white data-[state=checked]:bg-white data-[state=checked]:text-[#1E3A5F]" : ""}
                  />
                  {d}
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-2 items-start bg-orange-50 border border-orange-200 rounded-lg p-3 text-orange-900">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p className="text-xs">
              Le tue tariffe saranno copiate sui rispettivi giorni della settimana del prossimo anno.
              Il primo lunedì nell'intervallo che hai scelto sarà copiato sul primo lunedì del periodo di destinazione, e così via.
            </p>
          </div>
        </section>

        {/* SECTION 3: rooms / rate plans */}
        <section className="space-y-3 pt-2 border-t border-[#E8E2D9]">
          <h3 className="font-semibold text-[#1E3A5F] text-sm">
            Che tipologia di alloggio e di piano tariffario vuoi copiare?
          </h3>
          <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-[#1E3A5F]">
            <Checkbox checked={allRooms} onCheckedChange={(v) => setAllRooms(v === true)} data-testid="cr-all-rooms" />
            Seleziona tutti i piani tariffari per tutte le tipologie di alloggio
          </label>

          {!allRooms && (
            <div className="space-y-2" data-testid="cr-rooms-list">
              {rooms.length === 0 ? (
                <p className="text-xs text-[#635F59] italic">Nessuna camera disponibile.</p>
              ) : (
                rooms.map((r) => {
                  const sel = perRoom[r.id] || { allPlans: false, planIds: [] };
                  const isOpen = !!openRoom[r.id];
                  // Plans that apply to this room (or empty room_ids = applies to all)
                  const applicablePlans = ratePlans.filter(
                    (p) => p.active !== false && (!p.room_ids || p.room_ids.length === 0 || p.room_ids.includes(r.id))
                  );
                  return (
                    <div key={r.id} className="border border-[#E8E2D9] rounded-lg overflow-hidden">
                      <div className="bg-white px-3 py-2 flex items-center justify-between hover:bg-[#FDFBF7]">
                        <button
                          type="button"
                          onClick={() => setOpenRoom((p) => ({ ...p, [r.id]: !p[r.id] }))}
                          className="flex items-center gap-1.5 text-sm font-medium text-[#1E3A5F]"
                          data-testid={`cr-room-toggle-${r.slug || r.id}`}
                        >
                          {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                          <span className="capitalize">{typeof r.name === "string" ? r.name : (r.name?.it || r.slug)}</span>
                        </button>
                        <label className="flex items-center gap-1.5 cursor-pointer text-xs text-[#635F59]">
                          <Checkbox checked={sel.allPlans} onCheckedChange={() => toggleRoom(r.id)} data-testid={`cr-room-all-${r.slug || r.id}`} />
                          Tutti i piani
                        </label>
                      </div>
                      {isOpen && (
                        <div className="bg-[#FDFBF7]/50 px-3 py-2 grid grid-cols-2 md:grid-cols-3 gap-1.5">
                          {applicablePlans.length === 0 ? (
                            <span className="text-[11px] text-[#635F59] italic col-span-full">Nessun piano applicabile.</span>
                          ) : applicablePlans.map((p) => (
                            <label key={p.id} className="flex items-center gap-1.5 cursor-pointer text-xs text-[#1E3A5F]">
                              <Checkbox
                                checked={sel.allPlans || sel.planIds.includes(p.id)}
                                onCheckedChange={() => togglePlan(r.id, p.id)}
                                data-testid={`cr-plan-${r.slug || r.id}-${p.id}`}
                              />
                              {p.name}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}

          <label className="flex items-center gap-2 cursor-pointer text-xs text-[#635F59] pt-1">
            <Checkbox checked={overwrite} onCheckedChange={(v) => setOverwrite(v === true)} data-testid="cr-overwrite" />
            Sovrascrivi le tariffe già presenti nelle date di destinazione
          </label>
        </section>

        <DialogFooter className="pt-3 border-t border-[#E8E2D9] gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button
            onClick={apply}
            disabled={submitting}
            data-testid="cr-apply"
            className="bg-[#AC5D45] hover:bg-[#8A4936] text-white"
          >
            <Copy className="w-4 h-4 mr-1" />
            {submitting ? "Copio..." : "Copia tariffe"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
